import { useCallback, useEffect, useRef, useState } from "react";
import { CloudArrowUp, CheckCircle, Warning, CircleNotch, PencilSimple, Lock, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty, dayLabel } from "./registerApi";

// Phase 2: the supervisor posts the finished day to SAP by hand - one
// "Consumption for Cost Center" document per department per day, dated the
// day the material was used. A posted day is locked; changing it posts the
// difference as its own small document.

const StatusLine = ({ posting }) => (
  <div className="text-[11px] text-[#667085] space-y-0.5">
    {posting.sap_gac_id && (
      <div>
        SAP document <span className="font-mono font-bold text-[#344054]">{posting.sap_gac_id}</span>
      </div>
    )}
    <div>
      {posting.kind === "correction" ? "Correction" : "Consumption"} for {posting.entry_date}
      {posting.posted_by ? ` · by ${posting.posted_by}` : ""}
    </div>
  </div>
);

const Lines = ({ lines, testId }) => (
  <div className="divide-y divide-[#EAECF0] border border-[#EAECF0] rounded-sm bg-white" data-testid={testId}>
    {lines.map((l) => (
      <div key={l.product_id} className="flex items-center justify-between gap-3 px-2.5 py-1.5" data-testid={`${testId}-${l.product_id}`}>
        <div className="min-w-0">
          <div className="font-mono text-[13px] font-bold text-[#101828] truncate">{l.product_id}</div>
          <div className="text-[11px] text-[#667085] truncate">{l.description || "—"}</div>
        </div>
        <div className="text-sm font-bold tabular-nums text-[#1D2939] shrink-0">
          {qty(l.quantity)} <span className="text-[11px] font-bold text-[#667085]">{l.unit_code}</span>
        </div>
      </div>
    ))}
  </div>
);

const CorrectionForm = ({ preview, entryDate, onPosted, onCancel }) => {
  const posted = preview.posted_quantities || {};
  const [values, setValues] = useState(() =>
    Object.fromEntries(Object.entries(posted).map(([pid, q]) => [pid, String(q)])),
  );
  const [blockers, setBlockers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.correctionPreview(preview.department.id, entryDate,
      Object.entries(posted).map(([pid, q]) => `${pid}:${q}`).join(","))
      .then((d) => setBlockers(d.blockers.filter((b) => !b.includes("same as what SAP"))))
      .catch(() => setBlockers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deltas = Object.entries(values)
    .map(([pid, v]) => ({ pid, delta: Number(Number(v || 0) - (posted[pid] || 0)).toFixed(4) * 1 }))
    .filter((d) => Math.abs(d.delta) > 0.00001);
  const reductions = deltas.filter((d) => d.delta < 0);

  const submit = async () => {
    setSaving(true);
    try {
      const result = await api.postCorrection({
        department_id: preview.department.id,
        entry_date: entryDate,
        figures: Object.entries(values).map(([product_id, v]) => ({ product_id, quantity: Number(v || 0) })),
      });
      toast.success(`Correction sent to SAP (${result.postings.length} document${result.postings.length > 1 ? "s" : ""})`);
      onPosted();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="correction-form">
      <p className="text-xs text-[#667085]">
        Type today's full figure for {dayLabel(entryDate)} - only the extra goes to SAP, as its own consumption
        document. SAP cannot take a posted quantity back, so a day can only go up, and only once.
      </p>
      {Object.keys(posted).length === 0 && (
        <p className="text-xs text-[#B42318]" data-testid="correction-nothing-posted">Nothing is posted for this day.</p>
      )}
      {Object.entries(posted).map(([pid, q]) => (
        <div key={pid} className="flex items-center gap-2 bg-white border border-[#D0D5DD] rounded-sm px-2.5 py-2" data-testid={`correction-row-${pid}`}>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[13px] font-bold text-[#101828] truncate">{pid}</div>
            <div className="text-[11px] text-[#667085]">SAP holds {qty(q)} · can only go up</div>
          </div>
          <Input
            type="number"
            step="any"
            min={q}
            inputMode="decimal"
            className="w-24 h-11 text-right font-bold tabular-nums"
            value={values[pid] ?? ""}
            onChange={(e) => setValues((v) => ({ ...v, [pid]: e.target.value }))}
            data-testid={`correction-input-${pid}`}
          />
        </div>
      ))}
      {reductions.length > 0 && (
        <p className="text-xs font-bold text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm px-2 py-1.5" data-testid="correction-reduction">
          SAP cannot take a posted quantity back: {reductions.map((d) => `${d.pid} ${d.delta}`).join(", ")}.
          Leave it as it is or enter a higher figure.
        </p>
      )}
      {deltas.length > 0 && reductions.length === 0 && (
        <p className="text-xs font-bold text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1.5" data-testid="correction-delta">
          SAP will get: {deltas.map((d) => `${d.pid} +${d.delta}`).join(", ")}
        </p>
      )}
      {blockers.map((b) => (
        <p key={b} className="text-xs text-[#B42318]" data-testid="correction-blocker">{b}</p>
      ))}
      {confirming ? (
        <div className="space-y-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-2" data-testid="correction-confirm">
          <p className="text-xs text-[#B54708]">
            SAP will get {deltas.length} extra consumption line{deltas.length > 1 ? "s" : ""} for {dayLabel(entryDate)}:{" "}
            {deltas.map((d) => `${d.pid} +${d.delta}`).join(", ")}. This is the only correction this day can take. Send it?
          </p>
          <div className="flex gap-2">
            <Button className="flex-1 h-11 bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={saving} onClick={submit}
                    data-testid="confirm-correction">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowUp size={16} weight="bold" />} Yes, send the correction
            </Button>
            <Button variant="outline" className="h-11" onClick={() => setConfirming(false)} data-testid="cancel-correction-confirm">
              Not yet
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            className="flex-1 h-11 bg-[#0E7C86] hover:bg-[#0B6B74]"
            disabled={saving || deltas.length === 0 || reductions.length > 0 || blockers.length > 0}
            onClick={() => setConfirming(true)}
            data-testid="post-correction"
          >
            <CloudArrowUp size={16} weight="bold" /> Post correction
          </Button>
          <Button variant="outline" className="h-11" onClick={onCancel} data-testid="cancel-correction">Cancel</Button>
        </div>
      )}
    </div>
  );
};

const SettleUnknown = ({ posting, onSettled }) => {
  const [gacId, setGacId] = useState("");
  const [busy, setBusy] = useState(false);

  const settle = async (posted) => {
    setBusy(true);
    try {
      await api.resolveUnknown(posting.id, { posted, sap_gac_id: posted ? gacId : null });
      toast.success(posted ? "Marked as posted in SAP" : "Marked as not posted - the day is open again");
      onSettled();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="settle-unknown">
      <p className="text-xs text-[#B54708]">
        SAP stopped answering while this was being written, so it may or may not have taken the document. Check SAP's
        own Goods and Activity Confirmations for {posting.entry_date}, then tell the register what you found.
      </p>
      <Input
        className="h-10 font-mono"
        placeholder="SAP document ID (if it posted)"
        value={gacId}
        onChange={(e) => setGacId(e.target.value)}
        data-testid="settle-gac-id"
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={busy} onClick={() => settle(true)} data-testid="settle-posted">
          SAP did post it
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => settle(false)} data-testid="settle-not-posted">
          SAP did not post it
        </Button>
      </div>
    </div>
  );
};

export const SapPostingPanel = ({ register, onChanged }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [confirmingPost, setConfirmingPost] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const pollRef = useRef(null);
  // Switching days fires a fetch per day; whichever answers last would
  // otherwise win and paint another day's figures under this day's date.
  const wantedDate = useRef(null);

  const departmentId = register?.department?.id;
  const entryDate = register?.date;

  const load = useCallback(async () => {
    if (!departmentId || !entryDate) return;
    wantedDate.current = entryDate;
    try {
      const data = await api.postingPreview(departmentId, entryDate);
      if (wantedDate.current !== entryDate) return;
      setPreview(data);
    } catch (err) {
      if (wantedDate.current === entryDate) setPreview(null);
    } finally {
      if (wantedDate.current === entryDate) setLoading(false);
    }
  }, [departmentId, entryDate]);

  useEffect(() => { setCorrecting(false); setConfirmingPost(false); setLoading(true); load(); }, [load]);

  // A SAP document can take minutes; the browser polls instead of holding
  // a request open, so closing the tab never loses the posting.
  const live = preview?.live_posting;
  useEffect(() => {
    if (live?.status !== "pending") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await api.posting(live.id);
        if (fresh.status !== "pending") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (fresh.status === "posted") toast.success(`Posted to SAP · document ${fresh.sap_gac_id}`);
          if (fresh.status === "failed") toast.error(`SAP rejected the posting: ${fresh.error}`);
          if (fresh.status === "unknown") toast.warning("SAP timed out - the posting has to be settled against SAP");
          await load();
          onChanged?.();
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.status, live?.id]);

  const post = async () => {
    setPosting(true);
    try {
      await api.postDay(departmentId, entryDate);
      toast.info("Sent to SAP - this can take a minute for a big document");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setPosting(false);
      setConfirmingPost(false);
    }
  };

  if (!register) return null;
  if (!register.can_supervise) {
    const p = register.day_posting;
    return (
      <div className="rounded-sm border border-[#D0D5DD] bg-white px-3 py-2 text-xs text-[#667085]" data-testid="posting-readonly">
        {p?.status === "posted"
          ? <span className="text-[#027A48] font-bold">{dayLabel(entryDate)} is posted to SAP · document {p.sap_gac_id}</span>
          : p?.status === "pending"
            ? `${dayLabel(entryDate)} is being posted to SAP`
            : `${dayLabel(entryDate)} is not posted to SAP yet - your head or supervisor posts the day once it's done.`}
      </div>
    );
  }

  const failed = (preview?.history || []).find((p) => p.status === "failed" && p.kind === "daily");

  return (
    <div className="rounded-sm border border-[#D0D5DD] bg-[#F9FAFB] overflow-hidden" data-testid="sap-posting-panel">
      <div className="px-3 py-2 bg-white border-b border-[#EAECF0] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CloudArrowUp size={16} weight="bold" className="text-[#0E7C86] shrink-0" />
          <span className="font-heading text-sm font-bold text-[#101828]">SAP posting</span>
          <span className="text-[11px] text-[#667085] truncate">
            {dayLabel(entryDate)} · cost centre {preview?.department?.cost_center_id || register.department.cost_center_id}
          </span>
        </div>
        {live?.status === "posted" && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#027A48] bg-[#ECFDF3] border border-[#A6F4C5] rounded-sm px-1.5 py-0.5 shrink-0" data-testid="posting-status-badge">
            posted
          </span>
        )}
      </div>

      <div className="p-3 space-y-2">
        {loading ? (
          <div className="py-4 text-center"><CircleNotch size={18} className="animate-spin text-[#0E7C86] mx-auto" /></div>
        ) : !preview ? (
          <p className="text-xs text-[#667085]">Couldn't read the posting status.</p>
        ) : live?.status === "pending" ? (
          <div className="flex items-center gap-2 text-sm text-[#B54708]" data-testid="posting-pending">
            <CircleNotch size={16} className="animate-spin shrink-0" />
            Posting to SAP... this stays running even if you close the screen.
          </div>
        ) : live?.status === "unknown" ? (
          <SettleUnknown posting={live} onSettled={() => { load(); onChanged?.(); }} />
        ) : live?.status === "posted" ? (
          <div className="space-y-2" data-testid="posting-posted">
            <div className="flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-[#027A48] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#027A48]">{dayLabel(entryDate)} is posted to SAP</p>
                <StatusLine posting={live} />
                <p className="text-[11px] text-[#667085] flex items-center gap-1 mt-0.5">
                  <Lock size={11} weight="bold" /> This day's figures are locked.
                </p>
              </div>
            </div>
            {(preview.history || []).filter((p) => p.kind === "correction").map((p) => (
              <div key={p.id} className="text-[11px] text-[#667085] pl-6" data-testid={`correction-history-${p.id}`}>
                correction ({p.direction}) · {p.status}
                {p.sap_gac_id ? ` · ${p.sap_gac_id}` : ""}{p.error ? ` · ${p.error}` : ""}
              </div>
            ))}
            {correcting ? (
              <CorrectionForm
                preview={preview}
                entryDate={entryDate}
                onPosted={() => { setCorrecting(false); load(); onChanged?.(); }}
                onCancel={() => setCorrecting(false)}
              />
            ) : (
              <Button variant="outline" size="sm" className="w-full" onClick={() => setCorrecting(true)} data-testid="start-correction">
                <PencilSimple size={14} weight="bold" /> Add more consumption to this day
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {failed && (
              <p className="text-xs text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm px-2 py-1.5" data-testid="posting-failed">
                <Warning size={13} weight="bold" className="inline mr-1" />
                SAP rejected the last attempt: {failed.error}
              </p>
            )}
            {preview.lines.length > 0 ? (
              <>
                <p className="text-xs text-[#667085]">
                  One document, {preview.lines.length} line(s), dated {dayLabel(preview.document_date)} · from{" "}
                  <span className="font-mono font-bold">{preview.department.logistics_area_id || "no area set"}</span>
                </p>
                <Lines lines={preview.lines} testId="posting-preview-lines" />
              </>
            ) : (
              <p className="text-xs text-[#667085]" data-testid="posting-nothing">Nothing recorded for this day yet.</p>
            )}
            {preview.blockers.map((b) => (
              <p key={b} className="text-xs text-[#B54708] flex items-start gap-1.5" data-testid="posting-blocker">
                <Warning size={13} weight="bold" className="shrink-0 mt-0.5" /> {b}
              </p>
            ))}
            {confirmingPost ? (
              <div className="space-y-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-2" data-testid="posting-confirm">
                <p className="text-xs text-[#B54708]">
                  One SAP consumption document, {preview.lines.length} line(s), dated {dayLabel(preview.document_date)},
                  issued from <span className="font-mono font-bold">{preview.department.logistics_area_id}</span> against
                  cost centre <span className="font-mono font-bold">{preview.department.cost_center_id}</span>.
                  Once SAP has it, this day is locked and only a correction can change it. Send it?
                </p>
                <div className="flex gap-2">
                  <Button className="flex-1 h-11 bg-[#0E7C86] hover:bg-[#0B6B74]" disabled={posting} onClick={post}
                          data-testid="confirm-post-day">
                    {posting ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowUp size={16} weight="bold" />}
                    Yes, post to SAP
                  </Button>
                  <Button variant="outline" className="h-11" onClick={() => setConfirmingPost(false)} data-testid="cancel-post-day">
                    Not yet
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="w-full h-11 bg-[#0E7C86] hover:bg-[#0B6B74]"
                disabled={!preview.can_post || posting}
                onClick={() => setConfirmingPost(true)}
                data-testid="post-day-to-sap"
              >
                {failed ? <ArrowCounterClockwise size={16} weight="bold" /> : <CloudArrowUp size={16} weight="bold" />}
                {failed ? "Try posting again" : `Post ${dayLabel(entryDate)} to SAP`}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
